import { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Spin } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { customBaseUrl, httpGet } from "../../../store/http";

import {
  Typography,
  TextField,
  Box,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  Checkbox,
  ListItemText,
  ListSubheader,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
//gender in caps
const GENDERS = ["MALE", "FEMALE", "OTHER"];

const NewAdminUser = ({ showModal, setShowModal, handleSubmit }) => {
  const [roles, setRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const groupedPermissions = useMemo(() => {
    if (!permissions) return {};
    return permissions.reduce((acc, permission) => {
      const resource = permission.resource || "general";
      if (!acc[resource]) {
        acc[resource] = [];
      }
      acc[resource].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  const fetchRolesAndPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes] = await Promise.all([
        httpGet(`${customBaseUrl.authUrl}/api/v1/admin/users/roles`),
        httpGet(`${customBaseUrl.authUrl}/api/v1/admin/users/permissions`),
      ]);

      if (Array.isArray(rolesRes)) {
        setRoles(rolesRes);
      } else if (rolesRes.data && Array.isArray(rolesRes.data)) {
        setRoles(rolesRes.data);
      } else if (rolesRes.data && rolesRes.data.success) {
        setRoles(rolesRes.data.data);
      } else if (rolesRes.success) {
        setRoles(rolesRes.data);
      } else {
        console.error("Unexpected roles response structure:", rolesRes);
      }

      if (permissionsRes.data && permissionsRes.data.success) {
        setPermissions(permissionsRes.data.data);
      } else if (permissionsRes.success) {
        setPermissions(permissionsRes.data);
      } else {
        console.error(
          "Unexpected permissions response structure:",
          permissionsRes,
        );
      }
    } catch (error) {
      console.error("Failed to fetch roles or permissions", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showModal) {
      fetchRolesAndPermissions();
    }
  }, [showModal, fetchRolesAndPermissions]);

  const formik = useFormik({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      phoneNumber: "",
      password: "",
      roles: [],
      permissions: [],
      notes: "",
      gender: "",
      address: {
        street: "",
        city: "",
        state: "",
        country: "",
      },
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      lastName: Yup.string()
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      gender: Yup.string()
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      email: Yup.string().email("Invalid email address").required("Required"),
      phoneNumber: Yup.string("Phone number is required")
        .matches(/^\+?\d+$/, "Phone number is not valid")
        .length(11, "Phone number must be exactly 11 digits")
        .required("phone number is required"),
      password: Yup.string("Enter your password")
        .min(8, "Password should be of minimum 8 characters length")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/\d/, "Password must contain at least one number")
        .matches(
          /[^a-zA-Z0-9]/,
          "Password must contain at least one special character",
        )
        .required("Password is required"),
      dateOfBirth: Yup.date().required("Required"),
      roles: Yup.array().min(1, "At least one role is required").required("Required"),
      permissions: Yup.array(),
      address: Yup.object({
        street: Yup.string()
          .min(10, "Street address must be at least 10 characters")
          .required("Required"),
        city: Yup.string().required("Required"),
        state: Yup.string().required("Required"),
        country: Yup.string().required("Required"),
      }),
      notes: Yup.string(),
    }),
    onSubmit: (values) => {
      // console.log({ values });
      handleSubmit(values);
    },
  });

  return (
    <Modal
      open={showModal}
      closable={false}
      onCancel={() => setShowModal(false)}
      footer={null}
    >
      <Typography
        variant="h3"
        gutterBottom
        style={{ textAlign: "center", marginBottom: "30px" }}
      >
        Create New Admin User
      </Typography>
      {loading ? (
        <div style={{ textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  First Name
                </Typography>
                <TextField
                  id="firstName"
                  name="firstName"
                  fullWidth
                  size="small"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Last Name
                </Typography>
                <TextField
                  id="lastName"
                  name="lastName"
                  size="small"
                  fullWidth
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Email Address
                </Typography>
                <TextField
                  id="email"
                  name="email"
                  size="small"
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Phone Number
                </Typography>
                <TextField
                  id="phoneNumber"
                  name="phoneNumber"
                  size="small"
                  fullWidth
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Date of Birth
                </Typography>
                <TextField
                  fullWidth
                  id="dateOfBirth"
                  type="date"
                  size="small"
                  name="dateOfBirth"
                  value={formik.values.dateOfBirth}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.dateOfBirth &&
                    Boolean(formik.errors.dateOfBirth)
                  }
                  helperText={
                    formik.touched.dateOfBirth && formik.errors.dateOfBirth
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="medium">
                Gender
              </Typography>
              <TextField
                fullWidth
                id="gender"
                size="small"
                select
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected.length ? selected.charAt(0).toUpperCase() + selected.slice(1).toLowerCase() : "Select gender",
                }}
                name="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.gender && Boolean(formik.errors.gender)}
                helperText={formik.touched.gender && formik.errors.gender}
              >
                <MenuItem value="" disabled>
                  Select gender
                </MenuItem>
                {GENDERS.map((gender) => (
                  <MenuItem key={gender} value={gender}>
                    {gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="medium">
                Street Address
              </Typography>
              <TextField
                fullWidth
                id="address.street"
                name="address.street"
                size="small"
                value={formik.values.address.street}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.address?.street &&
                  Boolean(formik.errors.address?.street)
                }
                helperText={
                  formik.touched.address?.street &&
                  formik.errors.address?.street
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  City
                </Typography>
                <TextField
                  error={
                    formik.touched.address?.city &&
                    Boolean(formik.errors.address?.city)
                  }
                  helperText={
                    formik.touched.address?.city && formik.errors.address?.city
                  }
                  id="address.city"
                  size="small"
                  fullWidth
                  name="address.city"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address.city}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="medium">
                State
              </Typography>
              <TextField
                error={
                  formik.touched.address?.state &&
                  Boolean(formik.errors.address?.state)
                }
                helperText={
                  formik.touched.address?.state && formik.errors.address?.state
                }
                id="address.state"
                size="small"
                fullWidth
                name="address.state"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.address.state}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="medium">
                Country
              </Typography>
              <TextField
                error={
                  formik.touched.address?.country &&
                  Boolean(formik.errors.address?.country)
                }
                helperText={
                  formik.touched.address?.country &&
                  formik.errors.address?.country
                }
                id="address.country"
                fullWidth
                name="address.country"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.address.country}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="medium">
                Password
              </Typography>
              <TextField
                size="small"
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                fullWidth
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Roles
                </Typography>
                <TextField
                  select
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) =>
                      selected.length
                        ? selected.join(", ")
                        : "Please select roles",
                  }}
                  fullWidth
                  name="roles"
                  value={formik.values.roles || []}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="small"
                  error={formik.touched.roles && Boolean(formik.errors.roles)}
                  helperText={formik.touched.roles && formik.errors.roles}
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Checkbox checked={formik.values.roles.includes(role)} />
                      <ListItemText primary={role} />
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>

            <Grid item xs={12} md={12}>
              <Typography variant="subtitle1" fontWeight="medium">
                Permissions
              </Typography>
              <TextField
                error={
                  formik.touched.permissions &&
                  Boolean(formik.errors.permissions)
                }
                helperText={
                  formik.touched.permissions && formik.errors.permissions
                }
                select
                fullWidth
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected) => (
                    <span
                      style={{
                        fontSize: "0.575rem",
                        color: "#333",
                        maxWidth: "10rem",
                      }}
                    >
                      {selected.length
                        ? selected.map((s) => s.name).join(", ")
                        : "Please select permissions"}
                    </span>
                  ),
                }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="permissions"
                value={formik.values.permissions || []}
              >
                {Object.entries(groupedPermissions).map(([resource, perms]) => {
                  return [
                    <ListSubheader key={resource}>
                      {resource.toUpperCase()}
                    </ListSubheader>,
                    perms.map((permission) => (
                      <MenuItem key={permission.id} value={permission}>
                        <Checkbox
                          checked={(formik.values.permissions || []).some(
                            (perm) => perm.name === permission.name,
                          )}
                        />
                        <ListItemText primary={permission.name} />
                      </MenuItem>
                    )),
                  ];
                })}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium">
                Notes
              </Typography>
              <TextField
                fullWidth
                name="notes"
                multiline
                rows={2}
                value={formik.values.notes}
                onChange={formik.handleChange}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />
            </Grid>
          </Grid>

          <Box margin={4} display={"flex"} justifyContent={"center"}>
            <Button variant="contained" type="submit">
              Create New User
            </Button>
          </Box>
        </form>
      )}
    </Modal>
  );
};

export default NewAdminUser;
